import { apiClient } from './axios';

export interface CohortReport {
  totalPatients: number;
  ageStats: {
    mean: number;
    median: number;
    min: number;
    max: number;
  };
  genderDistribution: Record<string, number>;
  countyDistribution: Record<string, number>;
  urbanRuralDistribution: Record<string, number>;
  riskFactors: Record<string, number>;
  topComorbidities: Array<{ icd10: string; count: number }>;
}

export interface CasesReport {
  totalCases: number;
  byCategory: Record<string, number>;
  byTumorType: Record<string, number>;
  byBasis: Record<string, number>;
  byIncidenceYear: Record<string, number>;
  byLocation?: Record<string, number>;
  primaryVsMetastasis?: { primary: number; metastasis: number };
}

export interface IntervalsReport {
  admissionToHpResult: {
    median: number;
    iqr: [number, number];
    values: number[];
  };
  collectionToResult: {
    median: number;
    iqr: [number, number];
    values: number[];
  };
  dischargeToOncoConsult: {
    median: number;
    iqr: [number, number];
    values: number[];
  };
  hospitalizationDuration: {
    median: number;
    iqr: [number, number];
    values: number[];
  };
  admissionsPerPatient: {
    median: number;
    iqr: [number, number];
    values: number[];
  };
}

export interface CompletenessReport {
  documentsPerPatient: {
    mean: number;
    median: number;
  };
  missingKeyFields: Record<string, number>;
  alertsResolved: number;
  alertsUnresolved: number;
  patientsPending: number;
  consentDistribution: Record<string, number>;
}

export type ProgramType = 'DERMATO' | 'HEPATO';
export type ConsentStatus = 'TICKED' | 'SIGNED_NO_TICKS' | 'MISSING';

export interface ReportFilters {
  consent?: ConsentStatus[];
  from?: string;
  to?: string;
}

const buildQueryString = (filters: ReportFilters = {}): string => {
  const params = new URLSearchParams();

  if (filters.consent && filters.consent.length > 0) {
    params.append('consent', filters.consent.join(','));
  }

  if (filters.from) {
    params.append('from', filters.from);
  }

  if (filters.to) {
    params.append('to', filters.to);
  }

  return params.toString();
};

export const registryReportsApi = {
  getCohortReport: async (
    program: ProgramType,
    filters: ReportFilters = {},
  ): Promise<CohortReport> => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/cohort${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getCasesReport: async (
    program: ProgramType,
    filters: ReportFilters = {},
  ): Promise<CasesReport> => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/cases${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getIntervalsReport: async (
    program: ProgramType,
    filters: ReportFilters = {},
  ): Promise<IntervalsReport> => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/intervals${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getCompletenessReport: async (
    program: ProgramType,
    filters: ReportFilters = {},
  ): Promise<CompletenessReport> => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/completeness${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  downloadExcel: (program: ProgramType, filters: ReportFilters = {}): void => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/export.xlsx${query ? `?${query}` : ''}`;
    window.open(`${import.meta.env.VITE_API_URL}${url}`, '_blank');
  },

  downloadCSV: (
    program: ProgramType,
    sheet: string,
    filters: ReportFilters = {},
  ): void => {
    const query = buildQueryString(filters);
    const url = `/registry-reports/${program}/export/${sheet}.csv${query ? `?${query}` : ''}`;
    window.open(`${import.meta.env.VITE_API_URL}${url}`, '_blank');
  },
};
